import React, { useEffect, useState } from "react";
import Casino from "./../../assets/images/casino.svg";
import Sports from "./../../assets/images/sports.svg";
import FullSite from "./../../assets/images/full-site.svg";
import WalletSvg from "./../../assets/images/wallet.svg";
import Home from "../../assets/images/strikelogo2.svg";
import Promotions from "../../assets/images/promotion.svg";
import WASupportIcon from "../../assets/images/WAsupportIcon.png";
import { domain } from "../../constants/Branding";
import { getEnvVariable } from "../../constants/whitelabelEnv";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../models/RootState";
import { SiteConfig } from "../../models/SiteConfig";
import { useTranslation } from "../../hooks/useTranslation";
import { themes } from "./MyProfile/MyProfile";
import HomeIcon from '@mui/icons-material/Home';

const WhiteLabelLogo = getEnvVariable(domain, 'logo');
const BrandName = getEnvVariable(domain, 'BRAND_NAME');
const showSites = typeof BrandName === 'string' && BrandName.toLowerCase() !== 'winfast99' && (BrandName.toLowerCase() === 'hindu777' || BrandName.toLowerCase() === 'lotus365');
const isKhelo365days = typeof BrandName === 'string' && BrandName.toLowerCase() === 'khelo365days';
const isMomowin777 = typeof BrandName === 'string' && BrandName.toLowerCase() === 'momowin777';
const isBigwin2026 = typeof BrandName === 'string' && BrandName.toLowerCase() === 'bigwin2026';

type FooterItem = {
  icon: string | React.ComponentType<any>;
  label: string;
  path: string;
  /**
   * Returns true if the item should be shown (enabled)
   */
  isEnabled?: (siteConfig: SiteConfig) => boolean;
};

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(t("navigation.home"));
  const navigate = useNavigate();
  const location = useLocation();

  const siteConfig = useSelector(
    (state: RootState) => state.userDetails.siteConfig
  );

  const userDetails = useSelector(
    (state: RootState) => state.userDetails.userDetails
  );

  // Helper function to calculate brightness from hex color
  const getColorBrightness = (color: string): number => {
    if (!color) return 0;
    
    // Remove # if present
    const hex = color.replace('#', '');
    
    // Handle 8-digit hex (with alpha) - use first 6 digits for RGB
    const rgbHex = hex.length === 8 ? hex.substring(0, 6) : hex.length === 6 ? hex : hex.substring(0, 6);
    
    // Convert hex to RGB
    const r = parseInt(rgbHex.substring(0, 2), 16);
    const g = parseInt(rgbHex.substring(2, 4), 16);
    const b = parseInt(rgbHex.substring(4, 6), 16);
    
    // Calculate brightness using relative luminance formula
    return (r * 299 + g * 587 + b * 114) / 1000;
  };

  // Check if a predefined theme is selected
  const isPredefinedTheme = () => {
    const currentTheme = userDetails?.Theme?.toLowerCase();
    if (!currentTheme) return false;
    return Object.keys(themes).some(theme => theme.toLowerCase() === currentTheme);
  };

  // Determine if footer background is light or dark
  const isFooterLightTheme = () => {
    // If a predefined theme is selected, use dark theme (default)
    if (isPredefinedTheme()) {
      return false;
    }
    const primaryColor = siteConfig?.colorConfig?.primary;
    if (!primaryColor) return false;
    return getColorBrightness(primaryColor) > 128;
  };

  // Get footer background color (solid)
  const getFooterBackground = () => {
    // If a predefined theme is selected, use default solid background
    if (isPredefinedTheme()) {
      return "rgb(5, 19, 20)";
    }

    const primaryColor = siteConfig?.colorConfig?.primary;
    if (!primaryColor) {
      return "rgb(5, 19, 20)";
    }
    
    // Convert hex to rgb for solid background
    const hex = primaryColor.replace('#', '');
    const rgbHex = hex.length === 8 ? hex.substring(0, 6) : hex.length === 6 ? hex : hex.substring(0, 6);
    const r = parseInt(rgbHex.substring(0, 2), 16);
    const g = parseInt(rgbHex.substring(2, 4), 16);
    const b = parseInt(rgbHex.substring(4, 6), 16);
    
    // Return solid color
    return `rgb(${r}, ${g}, ${b})`;
  };

  // Footer text color based on background brightness
  const footerTextColor = isFooterLightTheme() ? '#000000' : '#FFFFFF';

const footerItems: FooterItem[] = [
  {
    icon: Casino,
      label: t("navigation.casino"),
    path: "casino",
    isEnabled: (siteConfig) => siteConfig.isCasinoEnabled, // show if casino is enabled
  },
  {
    icon: Sports,
      label: t("navigation.sports"),
    path: "sports",
    isEnabled: (siteConfig) => siteConfig.isSportsEnabled, // show if sports is enabled
  },
  {
    icon: isMomowin777 ? HomeIcon : WhiteLabelLogo,
      label: t("navigation.home"),
    path: "home",
    isEnabled: () => true, // always enabled
  },
  {
    icon: showSites ? FullSite : Promotions,
    label: showSites ? "Sites" : "Promos",
    path: "promotions",
    isEnabled: () => true,
  },
  {
    icon: WalletSvg,
      label: t("navigation.wallet"),
    path: "wallet",
    isEnabled: () => true,
  },
];

  const handleItemClick = (tab: FooterItem) => {
    setActiveTab(tab.label);
    navigate("/" + tab.path);
  };

  useEffect(() => {
    const currentItem = footerItems.find((item) =>
      location.pathname.includes(item.path)
    );
    if (currentItem) {
      setActiveTab(currentItem.label);
    }
  }, []);

  // ✅ Filter only enabled footer items
  const enabledFooterItems = footerItems.filter((item) => {
    if (typeof item.isEnabled === "function") {
      return item.isEnabled(siteConfig);
    }
    return item.isEnabled !== false; // default true
  });

  // Check if WhatsApp icon should be shown (only for Khelo365days on home, casino, sports pages)
  const showWhatsAppIcon = isKhelo365days && 
    (location.pathname.includes('home') || 
     location.pathname.includes('casino') || 
     location.pathname.includes('sports') ||
     location.pathname === '/');

  return (
    <>
      {/* Pulse animation keyframes */}
      <style>
        {`
          @keyframes pulse-scale {
            0%, 100% {
              transform: scale(1);
            }
            50% {
              transform: scale(1.1);
            }
          }
        `}
      </style>
      {/* WhatsApp Support Icon for Khelo365days */}
      {showWhatsAppIcon && (
        <a
          href={siteConfig?.contactInfo?.WhatsappSupport}
          target="_blank"
          rel="noopener noreferrer"
          className="fixed right-4 z-50 cursor-pointer"
          style={{
            top: "70%",
            transform: "translateY(-50%)",
            filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))',
          }}
        >
          <span
            style={{
              display: "inline-block",
              animation: "pulse-scale 2s ease-in-out infinite",
            }}
          >
            <img
              src={WASupportIcon}
              alt="WhatsApp Support"
              className="w-16 h-16 rounded-full"
            />
          </span>
        </a>
      )}
    <footer
      className={`fixed bottom-0 left-1/2 transform -translate-x-1/2 w-[95%] text-sm max-w-[25rem] font-medium rounded-2xl border-b-0 border border-border-color flex flex-col px-5 py-4 justify-center z-50`}
      style={{
        background: "rgb(5, 19, 20)",
        color: footerTextColor,
      }}
    >
      <nav className="flex justify-between w-full">
        {enabledFooterItems.map((item, index) => (
          <div
            key={index}
            className={`flex flex-col items-center flex-1 min-w-0 text-center`}
            onClick={() => handleItemClick(item)}
            style={{ cursor: "pointer", color: footerTextColor }}
          >
            {item.path === "home" && (isMomowin777 || isBigwin2026) ? (
              <HomeIcon
                className="self-center flex-shrink-0 w-[48px]"
                style={{
                  height: '22px',
                  color: footerTextColor,
                  filter: item.label === activeTab 
                    ? 'var(--filter-color)' 
                    : isFooterLightTheme()
                      ? "brightness(0)" // Black for light background
                      : "brightness(0) saturate(100%) invert(69%) sepia(0%) saturate(923%) hue-rotate(219deg) brightness(101%) contrast(99%)", // Gray for dark background
                }}
              />
            ) : typeof item.icon === 'string' ? (
              <img
                loading="lazy"
                src={item.icon}
                alt={item.label}
                className={`self-center flex-shrink-0 ${
                  item.path === "promotions"
                    ? "aspect-[1.45] w-[26px]"
                    : item.path === "wallet"
                    ? "w-5 rounded-lg aspect-[1.11]"
                    : item.path === "home"
                    ? "w-[48px]"
                    : "aspect-square w-[18px]"
                }`}
                style={{
                  filter: (() => {
                    if (item.label === activeTab) {
                      return item.label !== "Home" ? 'var(--filter-color)' : 'none';
                    }
                    if (item.label !== "Home") {
                      return isFooterLightTheme()
                        ? "brightness(0)" // Black for light background
                        : "brightness(0) saturate(100%) invert(69%) sepia(0%) saturate(923%) hue-rotate(219deg) brightness(101%) contrast(99%)"; // Gray for dark background
                    }
                    return "none";
                  })(),
                  height: item.label === "Home" ? '22px' : '',
                }}
              />
            ) : null}
            <div 
              className={`${item.path === "home" ? "mt-1" : "mt-2"} text-xs truncate w-full px-0.5`}
              title={item.label}
            >
              {item.label}
            </div>
          </div>
        ))}
      </nav>
    </footer>
    </>
  );
};

export default Footer;
